import {
  Experiment,
  ExperimentClient,
  Source,
  type ExperimentConfig,
  type ExperimentUser,
  type Variant,
} from '@amplitude/experiment-js-client'
import { isClient, isServer } from '../../utils/runtimeUtils'
import { experimentManager } from 'services/ExperimentManager/ExperimentManager'
import { useState } from 'react'
import {
  LANGUAGE_LOCALES,
  type LANGUAGES,
} from '../../locale/Translation.consts'
import { cc } from '../../locale/custom/countries'
import { type PricingItem } from '../../components/Pricing/types/pricingItem.types'
import { type MATERIAL_TYPES, TILE_SIZES } from '@mixtiles/web-backend-shared'
import { formatPricingItems } from '../../components/Pricing/PricingUtil'
import type { UserDeviceData } from 'services/UserDeviceContext'
import { logger } from 'services/logger'

export function getExperimentsUser(
  experimentsUserId: string | undefined,
  language: string,
  country: string,
  utmParams: Record<string, string>,
  userDeviceData: UserDeviceData,
  deviceId?: string
): ExperimentUser {
  return {
    user_id: experimentsUserId,
    device_id: deviceId,
    language: LANGUAGE_LOCALES[language as LANGUAGES],
    country: cc.en[country as keyof typeof cc.en],
    os: userDeviceData.osName,
    version: userDeviceData.osVersion,
    user_properties: {
      ...(utmParams ?? {}),
    },
  }
}

export function useInitExperimentClient({
  features,
  keys,
  experimentsUserId,
  language,
  utmParams,
  userDeviceData,
  forcedVariants,
  deviceId,
}: {
  features: Record<string, Variant>
  keys: Record<string, any>
  experimentsUserId: string | undefined
  language: string
  utmParams: Record<string, string>
  userDeviceData: UserDeviceData
  forcedVariants: Record<string, string>
  deviceId?: string
}) {
  const key = keys.amplitudeDeploymentKey
  let [client] = useState<ExperimentClient>(() => {
    const userProvider = {
      getUser: () =>
        getExperimentsUser(
          experimentsUserId,
          language,
          keys.ipCountry,
          utmParams,
          userDeviceData,
          deviceId
        ),
    }
    const experimentConfig: ExperimentConfig = {
      fetchTimeoutMillis: 10000,
      pollOnStart: false,
      initialVariants: features,
      userProvider,
      fetchOnStart: false,
      source: Source.InitialVariants, // Use source according to https://amplitude.com/docs/sdks/experiment-sdks/experiment-javascript#bootstrapping-variants
    }

    if (isServer()) {
      return new ExperimentClient(key, experimentConfig)
    }

    const client = Experiment.initializeWithAmplitudeAnalytics(
      key,
      experimentConfig
    )
    // Calling start so the experiments will be saved in the local storage
    // Note: This often fails in privacy browsers (Safari ITP, DuckDuckGo, etc.) - expected behavior
    client.start(userProvider.getUser()).catch((error) => {
      logger.warning('Failed to start experiment client', error)
    })
    return client
  })

  if (isClient()) {
    experimentManager.updateClient(client, forcedVariants)
  }

  return client
}

export function checkPriceConsistencyBetweenClassicAndMaterials(
  items: Record<string, PricingItem>,
  materialsToCheck: MATERIAL_TYPES[]
): boolean {
  items = formatPricingItems(items)
  // The idea here is to check if the price difference between the base price and the material prices
  // (from materialsToCheck) is consistent across all sizes. If that's the case,
  // we want to show the material as a color in the UI rather than as a material.
  const SIZES_TO_CHECK_PRICE_DIFF = [
    TILE_SIZES.SQUARE_8X8,
    TILE_SIZES.RECTANGLE_8X11,
    TILE_SIZES.RECTANGLE_11X8,
    TILE_SIZES.SQUARE_12X12,
    TILE_SIZES.RECTANGLE_12X16,
    TILE_SIZES.RECTANGLE_16X12,
    TILE_SIZES.SQUARE_20X20,
    TILE_SIZES.RECTANGLE_20X27,
    TILE_SIZES.RECTANGLE_27X20,
    TILE_SIZES.RECTANGLE_27X36,
    TILE_SIZES.RECTANGLE_36X27,
  ]
  let priceDifference: number | null = null

  for (const size of SIZES_TO_CHECK_PRICE_DIFF) {
    for (const material of materialsToCheck) {
      const materialTile = `${size}-${material}`

      if (items[size] && items[materialTile]) {
        const basePrice = items[size].price
        const materialPrice = items[materialTile].price
        const currentDifference = materialPrice - basePrice

        if (priceDifference === null) {
          // Set the first difference
          priceDifference = currentDifference
        } else if (priceDifference !== currentDifference) {
          // If any difference doesn't match, return false
          return false
        }
      }
    }
  }

  // If all differences matched, return true
  return true
}
